import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import {
  UserPlus,
  Users,
  User,
  Loader2,
  Mail,
  CheckCircle,
  AlertCircle,
  Briefcase,
  Shield,
  UserCog,
  Building2,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { SERVICE_CONFIG } from './ServiceTypeFilterCards';

const STATUS_COLORS = {
  PENDING_ADMIN_ASSIGNMENT: 'bg-amber-100 text-amber-700',
  ASSIGNED: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-purple-100 text-purple-700',
  ON_HOLD: 'bg-orange-100 text-orange-700',
  COMPLETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

const REP_TYPE_CONFIG = {
  rep1: { label: 'Internal Rep', icon: Shield, color: 'bg-blue-100 text-blue-700' },
  rep2: { label: 'Senior Rep', icon: UserCog, color: 'bg-purple-100 text-purple-700' },
  rep3: { label: 'Partner Rep', icon: Building2, color: 'bg-amber-100 text-amber-700' },
  agent: { label: 'Agent', icon: User, color: 'bg-indigo-100 text-indigo-700' },
};

export default function AdminStudentRelationshipsTab({ student, counselors = [], agents = [], onUpdate }) {
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignRequest, setAssignRequest] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [assigning, setAssigning] = useState(false);

  const serviceRequests = student?.serviceRequests || [];

  const handleOpenAssignDialog = (request) => {
    setAssignRequest(request);
    setSelectedUserId(request.assignedCounselor?._id || request.assignedCounselor?.userId || '');
    setAssignDialogOpen(true);
  };

  const handleAssign = async () => {
    if (!selectedUserId || !assignRequest) {
      toast.error('Please select a counselor');
      return;
    }

    setAssigning(true);
    try {
      const requestId = assignRequest.serviceRequestId || assignRequest.requestId || assignRequest._id;
      await adminAPI.assignServiceRequest(requestId, {
        counselorId: selectedUserId,
      });
      toast.success('Counselor assigned successfully');
      setAssignDialogOpen(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to assign:', error);
      toast.error(error.response?.data?.error || 'Failed to assign counselor');
    } finally {
      setAssigning(false);
    }
  };

  // Referring representative/agent info
  const referredBy = student?.referredBy || student?.assignedAgent;
  const referredByRole = referredBy?.role;
  const repConfig = REP_TYPE_CONFIG[referredByRole] || REP_TYPE_CONFIG.agent;
  const RepIcon = repConfig?.icon || User;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Service Teams</h3>
        <p className="text-sm text-gray-500">Per-service counselor assignments and team overview</p>
      </div>

      {/* Referring Representative / Agent */}
      {referredBy && (
        <Card className="p-5 border border-indigo-100 bg-indigo-50/30">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <RepIcon className="w-5 h-5 text-indigo-600" />
            </div>
            <h4 className="font-semibold text-gray-900">Referring Representative</h4>
          </div>
          <div className="flex items-center gap-4">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="text-white font-semibold bg-gradient-to-br from-indigo-400 to-indigo-600">
                {referredBy.firstName?.[0]}{referredBy.lastName?.[0]}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="font-semibold text-gray-900">
                {referredBy.firstName} {referredBy.lastName}
              </p>
              {referredBy.email && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600 mt-0.5">
                  <Mail className="w-3.5 h-3.5 text-gray-400" />
                  {referredBy.email}
                </div>
              )}
            </div>
            <Badge className={repConfig.color}>
              <RepIcon className="w-3 h-3 mr-1" />
              {repConfig.label}
            </Badge>
          </div>
        </Card>
      )}

      {/* Service Teams Grid */}
      {serviceRequests.length > 0 ? (
        <div className="space-y-3">
          {serviceRequests.map((request) => {
            const serviceConfig = SERVICE_CONFIG[request.serviceType] || {};
            const counselor = request.assignedCounselor;
            const statusLabel = request.status?.replace(/_/g, ' ') || 'Unknown';

            return (
              <Card
                key={request.serviceRequestId || request._id}
                className="p-4 border border-stone-200 hover:border-stone-300 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Service icon */}
                    <div className={`p-2.5 rounded-lg ${serviceConfig.bgColor || 'bg-gray-100'}`}>
                      {serviceConfig.icon ? (
                        <img src={serviceConfig.icon} alt="" className="w-5 h-5" />
                      ) : (
                        <Briefcase className={`w-5 h-5 ${serviceConfig.textColor || 'text-gray-600'}`} />
                      )}
                    </div>

                    {/* Service info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">
                        {serviceConfig.name || request.serviceType?.replace(/_/g, ' ')}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={`text-xs ${STATUS_COLORS[request.status] || 'bg-gray-100 text-gray-700'}`}>
                          {statusLabel}
                        </Badge>
                        {request.createdAt && (
                          <span className="text-xs text-gray-400">
                            {format(new Date(request.appliedAt || request.createdAt), 'MMM dd, yyyy')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Assigned Counselor */}
                  <div className="flex items-center gap-3 ml-4">
                    {counselor ? (
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs text-white font-medium bg-gradient-to-br from-green-400 to-green-600">
                            {counselor.firstName?.[0]}{counselor.lastName?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {counselor.firstName} {counselor.lastName}
                          </p>
                          <p className="text-xs text-green-600">Counselor</p>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">No counselor</span>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenAssignDialog(request)}
                      className="ml-2"
                    >
                      <UserPlus className="w-3.5 h-3.5 mr-1" />
                      {counselor ? 'Change' : 'Assign'}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="p-8 border border-dashed border-stone-300 bg-stone-50/50">
          <div className="text-center">
            <Briefcase className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 font-medium">No Service Requests</p>
            <p className="text-sm text-gray-400 mt-1">
              This student hasn't applied for any services yet
            </p>
          </div>
        </Card>
      )}

      {/* Student Info Summary */}
      <Card className="p-5 border border-stone-200">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-purple-100 rounded-lg">
            <User className="w-5 h-5 text-purple-600" />
          </div>
          <h4 className="font-semibold text-gray-900">Student Information</h4>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Student ID</p>
            <p className="font-medium text-gray-900 text-sm">{student?.studentId || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Status</p>
            <Badge className={student?.onboardingCompleted ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
              {student?.onboardingCompleted ? 'Active' : 'Onboarding'}
            </Badge>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Registered</p>
            <p className="font-medium text-gray-900 text-sm">
              {student?.createdAt ? format(new Date(student.createdAt), 'MMM dd, yyyy') : '-'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Total Services</p>
            <p className="font-medium text-gray-900 text-sm">{serviceRequests.length}</p>
          </div>
        </div>
      </Card>

      {/* Assign Counselor Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-500" />
              Assign Counselor
            </DialogTitle>
            <DialogDescription>
              Select a counselor for this service request.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {assignRequest && (
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-medium text-sm">
                  {SERVICE_CONFIG[assignRequest.serviceType]?.name || assignRequest.serviceType?.replace(/_/g, ' ')}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Student: {student?.firstName || student?.user?.firstName} {student?.lastName || student?.user?.lastName}
                </p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                Select Counselor
              </label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a counselor" />
                </SelectTrigger>
                <SelectContent>
                  {counselors.map((person) => (
                    <SelectItem key={person.userId || person._id} value={person.userId || person._id}>
                      <div className="flex items-center gap-2">
                        <span>{person.firstName} {person.lastName}</span>
                        {person.email && (
                          <span className="text-xs text-gray-500">({person.email})</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAssign}
                disabled={assigning || !selectedUserId}
                className="bg-green-600 hover:bg-green-700"
              >
                {assigning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Assign Counselor
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
